// Favourite trips, and how they sit beside commute rules.
//
// A favourite is a trip somebody wants one tap away: two stations and nothing
// else. A commute rule is the same two stations plus a window and an alert
// behaviour. They are one list to the person (a trip, with alerts on or off),
// and two stores underneath, because rules are validated field by field by the
// server's subscribe body and a rule with no window is not something it takes.
//
// So this file merges them by their ends. A trip that is only a rule is shown;
// a favourite with no rule has alerts off; a favourite with a rule has them on.
//
// PURE. No Vue, no storage: `app-state.js` owns the list on disk.

/** A strip, not a library. */
export const MAX_FAVOURITES = 8;

/** `1034>1234`: a trip is its two ends, in that order. */
export function tripKey(from, to) {
  return `${Number(from)}>${Number(to)}`;
}

/** One stored favourite, or null where it is not two different stations. */
export function cleanFavourite(row) {
  const from = Number(row?.from);
  const to = Number(row?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from <= 0 || to <= 0 || from === to) return null;
  return {
    from,
    to,
    fromName: String(row.fromName ?? ''),
    toName: String(row.toName ?? ''),
    savedAt: Number.isFinite(Number(row.savedAt)) ? Number(row.savedAt) : 0,
  };
}

/** The trip the other way round. Names travel with their ends. */
export function reverseTrip(trip) {
  return {
    ...trip,
    from: trip.to,
    to: trip.from,
    fromName: trip.toName,
    toName: trip.fromName,
  };
}

/**
 * Favourites and rules as one list of `{ key, from, to, fromName, toName,
 * favourite, rule }`. `rule` is the active rule for those ends, if any.
 *
 * A rule that is switched off is not alerts-on, but it is still a saved trip,
 * so it stays in the list.
 */
export function mergeTrips(favourites = [], rules = []) {
  const byKey = new Map();
  for (const f of favourites) {
    const c = cleanFavourite(f);
    if (c) byKey.set(tripKey(c.from, c.to), { ...c, key: tripKey(c.from, c.to), favourite: true, rule: null });
  }
  for (const r of rules) {
    const from = Number(r?.from);
    const to = Number(r?.to);
    if (!(from > 0) || !(to > 0) || from === to) continue;
    const key = tripKey(from, to);
    const held = byKey.get(key) ?? {
      key, from, to, fromName: String(r.fromName ?? ''), toName: String(r.toName ?? ''),
      savedAt: 0, favourite: false, rule: null,
    };
    // The first active rule wins; an inactive one only fills an empty slot.
    if (!held.rule || (held.rule.active === false && r.active !== false)) held.rule = r;
    byKey.set(key, held);
  }
  return [...byKey.values()];
}

/** Alerts are on when there is a rule that is switched on. */
export function alertsOn(trip) {
  return Boolean(trip?.rule && trip.rule.active !== false);
}

/**
 * The strip's order: trips whose window is open now first, then those that
 * leave from the station the person is at, then the most recently saved.
 *
 * @param {{minutes:number, day:number}} [at] local minute of day and weekday
 * @param {number} [hereStopId] the station the board is on
 */
export function orderTrips(trips, { at = null, hereStopId = 0 } = {}) {
  const open = (t) => {
    const r = t.rule;
    if (!r || r.active === false || !at) return false;
    const toMin = (s) => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(s ?? '')); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
    const a = toMin(r.start);
    const b = toMin(r.end);
    if (a === null || b === null) return false;
    if (Array.isArray(r.days) && !r.days.includes(at.day)) return false;
    return at.minutes >= a && at.minutes <= b;
  };
  const score = (t) => (open(t) ? 0 : 2) + (Number(hereStopId) === t.from ? 0 : 1);
  return [...trips].sort((x, y) => score(x) - score(y) || (y.savedAt || 0) - (x.savedAt || 0));
}
